// ============================================
// Kelvin → RGB Conversion Utilities
// ============================================
//
// Converts color temperature (Kelvin) to linear RGB using the
// Tanner Helland approximation of the Planckian locus.
// Used by the lighting system when emission mode is 'temperature'.

/**
 * Convert color temperature in Kelvin to linear RGB (0–1).
 *
 * Based on Tanner Helland's approximation of the CIE 1931 Planckian locus.
 * Accurate within ±2% for the 1000K–40000K range.
 *
 * @param kelvin Color temperature in Kelvin (clamped to 1000–40000).
 * @returns `[r, g, b]` in 0–1 linear range.
 */
export function kelvinToRGB(kelvin: number): [number, number, number] {
  const temp = Math.max(1000, Math.min(40000, kelvin)) / 100;

  let r: number;
  let g: number;
  let b: number;

  // ---- Red ----
  if (temp <= 66) {
    r = 255;
  } else {
    r = 329.698727446 * Math.pow(temp - 60, -0.1332047592);
    r = Math.max(0, Math.min(255, r));
  }

  // ---- Green ----
  if (temp <= 66) {
    g = 99.4708025861 * Math.log(temp) - 161.1195681661;
  } else {
    g = 288.1221695283 * Math.pow(temp - 60, -0.0755148492);
  }
  g = Math.max(0, Math.min(255, g));

  // ---- Blue ----
  if (temp >= 66) {
    b = 255;
  } else if (temp <= 19) {
    b = 0;
  } else {
    b = 138.5177312231 * Math.log(temp - 10) - 305.0447927307;
    b = Math.max(0, Math.min(255, b));
  }

  // Normalize to 0–1 linear
  return [r / 255, g / 255, b / 255];
}

/**
 * Apply green–magenta tint offset to a Kelvin-derived RGB color.
 *
 * In cinematography, tint is the shift perpendicular to the Planckian locus
 * (green ↔ magenta). This is a simplified linear approximation.
 *
 * @param rgb Base linear RGB from `kelvinToRGB`.
 * @param tint Tint offset: −1 = full green, +1 = full magenta, 0 = neutral.
 * @returns Modified `[r, g, b]` clamped to 0–1.
 */
export function applyTint(
  rgb: [number, number, number],
  tint: number,
): [number, number, number] {
  const t = Math.max(-1, Math.min(1, tint));
  return [
    Math.max(0, Math.min(1, rgb[0] + t * 0.10)),
    Math.max(0, Math.min(1, rgb[1] - t * 0.15)),
    Math.max(0, Math.min(1, rgb[2] + t * 0.10)),
  ];
}

/**
 * Resolve the effective linear RGB color for a light based on emission mode.
 *
 * - `'color'` → returns the direct `color` value.
 * - `'temperature'` → converts `temperature` (K) + `tint` to RGB.
 *
 * @param emissionMode `'color'` or `'temperature'`
 * @param color Direct RGB color (linear 0–1), used when mode is `'color'`.
 * @param temperature Color temperature in Kelvin, used when mode is `'temperature'`.
 * @param tint Green–magenta tint, used when mode is `'temperature'`.
 * @returns `[r, g, b]` linear 0–1.
 */
export function resolveEmissionColor(
  emissionMode: 'color' | 'temperature',
  color: [number, number, number],
  temperature: number,
  tint: number,
): [number, number, number] {
  if (emissionMode === 'temperature') {
    return applyTint(kelvinToRGB(temperature), tint);
  }
  return color;
}

// ---- Common Kelvin presets ----

/** Well-known color temperature presets (Kelvin). */
export const KELVIN_PRESETS = {
  /** Candle light / very warm */
  CANDLE: 1850,
  /** 40W incandescent bulb */
  INCANDESCENT: 2400,
  /** Warm white LED / halogen */
  WARM_WHITE: 3000,
  /** Neutral white fluorescent */
  NEUTRAL_WHITE: 4000,
  /** Daylight (D50 illuminant) */
  DAYLIGHT_D50: 5000,
  /** Noon daylight (D65 illuminant) */
  DAYLIGHT_D65: 6500,
  /** Overcast sky */
  OVERCAST: 7500,
  /** Blue sky / shade */
  BLUE_SKY: 10000,
  /** Clear north sky */
  NORTH_SKY: 15000,
} as const;
